type Problem = {
  id: string;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  description: string;
  examples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  starterCode: Record<string, string>;
  testCases: Array<{
    input: string;
    output: string;
  }>;
};

export const stringProblems: Problem[] = [
  {
    id: 'strings-1',
    title: 'Valid Palindrome',
    difficulty: 'Easy',
    description: `A phrase is a palindrome if, after converting all uppercase letters into lowercase letters and removing all non-alphanumeric characters, it reads the same forward and backward.

Alphanumeric characters include letters and numbers.

Given a string \`s\`, return \`true\` if it is a palindrome, or \`false\` otherwise.

**Constraints:**
- 1 <= s.length <= 2 * 10^5
- \`s\` consists only of printable ASCII characters`,
    examples: [
      {
        input: 's = "A man, a plan, a canal: Panama"',
        output: 'true',
        explanation: '"amanaplanacanalpanama" is a palindrome.'
      },
      {
        input: 's = "race a car"',
        output: 'false',
        explanation: '"raceacar" is not a palindrome.'
      }
    ],
    starterCode: {
      typescript: `function isPalindrome(s: string): boolean {
    // Write your code here
}`
    },
    testCases: [
      {
        input: 's = "A man, a plan, a canal: Panama"',
        output: 'true'
      },
      {
        input: 's = "race a car"',
        output: 'false'
      }
    ]
  },
  {
    id: 'strings-2',
    title: 'Longest Common Prefix',
    difficulty: 'Easy',
    description: `Write a function to find the longest common prefix string amongst an array of strings.

If there is no common prefix, return an empty string \`""\`.

**Constraints:**
- 1 <= strs.length <= 200
- 0 <= strs[i].length <= 200
- \`strs[i]\` consists of only lowercase English letters`,
    examples: [
      {
        input: 'strs = ["flower","flow","flight"]',
        output: '"fl"',
        explanation: 'The common prefix between all three strings is "fl".'
      },
      {
        input: 'strs = ["dog","racecar","car"]',
        output: '""',
        explanation: 'There is no common prefix among the input strings.'
      }
    ],
    starterCode: {
      typescript: `function longestCommonPrefix(strs: string[]): string {
    // Write your code here
}`
    },
    testCases: [
      {
        input: 'strs = ["flower","flow","flight"]',
        output: '"fl"'
      },
      {
        input: 'strs = ["dog","racecar","car"]',
        output: '""'
      }
    ]
  },
  {
    id: 'strings-3',
    title: 'Group Anagrams',
    difficulty: 'Medium',
    description: `Given an array of strings \`strs\`, group the anagrams together. You can return the answer in any order.

An Anagram is a word or phrase formed by rearranging the letters of a different word or phrase, typically using all the original letters exactly once.

**Constraints:**
- 1 <= strs.length <= 10^4
- 0 <= strs[i].length <= 100
- \`strs[i]\` consists of lowercase English letters`,
    examples: [
      {
        input: 'strs = ["eat","tea","tan","ate","nat","bat"]',
        output: '[["bat"],["nat","tan"],["ate","eat","tea"]]',
        explanation: 'The words can be grouped by their anagrams.'
      }
    ],
    starterCode: {
      typescript: `function groupAnagrams(strs: string[]): string[][] {
    // Write your code here
}`
    },
    testCases: [
      {
        input: 'strs = ["eat","tea","tan","ate","nat","bat"]',
        output: '[["bat"],["nat","tan"],["ate","eat","tea"]]'
      },
      {
        input: 'strs = [""]',
        output: '[[""]]'
      }
    ]
  },
  {
    id: 'strings-4',
    title: 'Longest Palindromic Substring',
    difficulty: 'Medium',
    description: `Given a string \`s\`, return the longest palindromic substring in \`s\`.

**Constraints:**
- 1 <= s.length <= 1000
- \`s\` consist of only digits and English letters`,
    examples: [
      {
        input: 's = "babad"',
        output: '"bab"',
        explanation: '"aba" is also a valid answer.'
      },
      {
        input: 's = "cbbd"',
        output: '"bb"'
      }
    ],
    starterCode: {
      typescript: `function longestPalindrome(s: string): string {
    // Write your code here
}`
    },
    testCases: [
      {
        input: 's = "babad"',
        output: '"bab"'
      },
      {
        input: 's = "cbbd"',
        output: '"bb"'
      }
    ]
  },
  {
    id: 'strings-5',
    title: 'Regular Expression Matching',
    difficulty: 'Hard',
    description: `Given an input string 's' and a pattern 'p', implement regular expression matching with support for '.' and '*' where:

- '.' Matches any single character.
- '*' Matches zero or more of the preceding element.

The matching should cover the entire input string (not partial).

**Constraints:**
- 1 <= s.length <= 20
- 1 <= p.length <= 20
- 's' contains only lowercase English letters
- 'p' contains only lowercase English letters, '.' and '*'
- It is guaranteed for each appearance of the character '*', there will be a previous valid character to match`,
    examples: [
      {
        input: 's = "aa", p = "a"',
        output: 'false',
        explanation: '"a" does not match the entire string "aa".'
      },
      {
        input: 's = "aa", p = "a*"',
        output: 'true',
        explanation: '"a*" means zero or more of \'a\', which matches "aa".'
      },
      {
        input: 's = "ab", p = ".*"',
        output: 'true',
        explanation: '".*" means zero or more of any character.'
      }
    ],
    starterCode: {
      typescript: `function isMatch(s: string, p: string): boolean {
    // Write your code here
}`
    },
    testCases: [
      {
        input: 's = "aa", p = "a"',
        output: 'false'
      },
      {
        input: 's = "aa", p = "a*"',
        output: 'true'
      },
      {
        input: 's = "ab", p = ".*"',
        output: 'true'
      }
    ]
  }
];
